import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PERSONA_ONBOARDING, UserRole } from 'src/constants';
import type { UserLevelProgress } from 'src/openapi';
import apiClient from 'src/helpers/apiClient';
import { LS_KEY_ON_AUTH_REDIRECT_PATHNAME } from 'src/constants/localStorageKeys';
import {
  login,
  redeemLoginHandoff,
  userSelf,
  updateSelf,
  logout,
  setDashboardPersona,
} from '../actions/auth';
import { onTourDone } from '../actions/employerLayout';
import { AuthState } from '../types/auth';

// Which app shell the UI should present. Kept as one mapping because the two
// call sites below drifted apart before — a role added to one and not the other
// leaves UI_ENV null, and the navbar then silently falls back to the candidate
// menu.
const resolveUIEnv = (userType?: string | null): AuthState['UI_ENV'] => {
  if (userType === 'Applicant' || userType === 'Learner') {
    return UserRole.Applicant;
  }
  if (userType === UserRole.Tpo) return UserRole.Tpo;
  if (userType === UserRole.Employer) return UserRole.Employer;
  if (userType === UserRole.PlacementPartner) return UserRole.PlacementPartner;
  if (userType === UserRole.Faculty) return UserRole.Faculty;
  return null;
};

const initialState: AuthState = {
  user: {},
  xpPulse: null,
  isFetchingUser: false,
  token: '',
  isLoginSuccess: false,
  UI_ENV: null,
  isUploadingPicture: false,
};

/* eslint-disable  */
/** Signed out on this device: the stored tokens go, and so does the user. */
const clearSession = (state: AuthState) => {
  localStorage.removeItem('loginToken');
  localStorage.removeItem('secondaryToken');
  localStorage.removeItem(LS_KEY_ON_AUTH_REDIRECT_PATHNAME);
  localStorage.removeItem('userType');
  state.user = {};
  state.isLoginSuccess = false;
  state.token = '';
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Without asking the server: for a stored token that may have expired,
    // where the API's sign-out would only fail.
    clearStoredSession: (state) => clearSession(state),
    setLoginToken: (state, action) => {
      if (!JSON.parse(localStorage.getItem('isAdminMode') || 'false')) {
        localStorage.setItem('loginToken', action.payload);
      }
      apiClient.setApiKey(action.payload as string);
      state.token = action.payload;
    },
    decrementCredits: (state, action: PayloadAction<number>) => {
      if (state.user.credits !== undefined) {
        state.user.credits = state.user.credits - action.payload;
      }
    },
    /** use this function to set new value to credits */
    setCredits: (state, action: PayloadAction<number>) => {
      state.user.credits = action.payload;
    },
    /** Joining a class shows the sidebar's Classes group without a reload. */
    markJoinedClass: (state) => {
      state.user.has_joined_classes = true;
    },
    /**
     * New XP balance, and where it lands on the platform ladder.
     *
     * Both absolute rather than deltas — an `XpChange` push can arrive twice
     * (a round award and a topper bonus land separately, and a result screen
     * can be reopened), and an increment applied twice is a wrong number that
     * never corrects itself. `level` is optional so an older server that pushes
     * only the balance leaves the bar showing the last known band instead of
     * blanking it.
     */
    setXp: (
      state,
      action: PayloadAction<{
        xp: number;
        level?: UserLevelProgress | null;
        change?: number;
        levelledUp?: number[];
      }>,
    ) => {
      state.user.xp = action.payload.xp;
      if (action.payload.level)
        state.user.level = { ...state.user.level, ...action.payload.level };
      // The signal the navbar animates off. `levelled_up` has been on the
      // socket since the ladder shipped and nothing read it, so crossing a rung
      // produced no acknowledgement at all — the payoff moment of a reward loop
      // arrived silently.
      const change = action.payload.change ?? 0;
      const levelledUp = action.payload.levelledUp ?? [];
      if (change > 0 || levelledUp.length) {
        state.xpPulse = {
          id: Date.now(),
          change,
          levelledUp,
          levelName:
            action.payload.level?.level_name ?? state.user.level?.level_name,
        };
      }
    },
    /** Played and done. Cleared by the bar once its animation finishes. */
    clearXpPulse: (state) => {
      state.xpPulse = null;
    },
    setUIEnv: (state, action: PayloadAction<AuthState['UI_ENV']>) => {
      state.UI_ENV = action.payload;
    },
    updateUserPremiumProfileExpiresOn: (state, action) => {
      state.user.premium_profile_expires_on = action.payload;
    },
  },
  extraReducers: (builder) => {
    const signedIn = (
      state: AuthState,
      action: PayloadAction<
        ReturnType<typeof login.fulfilled>['payload'] | undefined
      >,
    ) => {
      // Backend now returns both `access_token` (canonical) and `token`
      // (legacy alias, same value). Prefer access_token so web is already
      // forward-compatible when the `token` alias is eventually dropped.
      const payload = action.payload as typeof action.payload & {
        access_token?: string;
      };
      const token = payload?.access_token ?? (payload as any)?.token;
      if (token && payload?.user) {
        apiClient.setApiKey(token);
        if (!JSON.parse(localStorage.getItem('isAdminMode') || 'false')) {
          localStorage.setItem('loginToken', token);
        }
        localStorage.setItem('userType', payload.user.user_type ?? '');
        state.user = payload.user;
        state.token = token;
        state.isLoginSuccess = true;
      }
    };
    builder.addCase(login.fulfilled, signedIn);
    builder.addCase(redeemLoginHandoff.fulfilled, signedIn);

    builder
      .addCase(userSelf.pending, (state) => {
        state.isFetchingUser = true;
      })
      .addCase(userSelf.fulfilled, (state, action) => {
        state.user = action.payload;
        state.token = JSON.parse(localStorage.getItem('isAdminMode') || 'false')
          ? (localStorage.getItem('secondaryToken') as string)
          : (localStorage.getItem('loginToken') as string);
        state.isLoginSuccess = true;
        localStorage.setItem('userType', action.payload.user_type ?? '');
        state.UI_ENV = resolveUIEnv(action.payload?.user_type);
        state.isFetchingUser = false;
      })
      .addCase(userSelf.rejected, (state, action) => {
        state.isFetchingUser = false;
        if ([401, 403].includes((action?.payload as any)?.status ?? 0)) {
          // Deliberately keeps LS_KEY_ON_AUTH_REDIRECT_PATHNAME. This is the
          // expiry the return path exists for: the interceptor stores it in
          // `handleAuthFailure` moments before this reducer runs, so clearing
          // it here would lose the destination at every `dispatch(userSelf())`
          // call site except `routes/index.tsx`, which stores it again
          // afterwards. `resolvePostAuthRoute` clears the key
          // when it consumes it and `logout.fulfilled` clears it on a
          // deliberate sign-out, so it cannot go stale by being left here.
          localStorage.removeItem('loginToken');
          localStorage.removeItem('secondaryToken');
          localStorage.removeItem('userType');
          state.token = '';
          state.isLoginSuccess = false;
          state.user = {};
        }
      });

    builder.addCase(updateSelf.fulfilled, (state, action) => {
      state.UI_ENV = resolveUIEnv(action.payload?.user_type);
      // Merged, not replaced: the update's response leaves out what /self
      // works out per request (roll_number, has_joined_classes, xp...).
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem('userType', action.payload.user_type ?? '');
    });
    builder.addCase(setDashboardPersona.fulfilled, (state, action) => {
      state.user.dashboard_persona = action.payload.persona;
      state.user.dashboard_goal = action.payload.goal;
      // Mirrors what the server stamped, so the questionnaire closes without
      // waiting on a /self refetch.
      if (!state.user.viewed_onboardings?.includes(PERSONA_ONBOARDING)) {
        state.user.viewed_onboardings = [
          ...(state.user.viewed_onboardings ?? []),
          PERSONA_ONBOARDING,
        ];
      }
    });
    builder.addCase(onTourDone.fulfilled, (state, action) => {
      const name = action.meta.arg?.name;
      if (!name) return;
      if (!state.user.viewed_tour) {
        state.user.viewed_tour = [name];
      } else if (!state.user.viewed_tour.includes(name)) {
        state.user.viewed_tour = [...state.user.viewed_tour, name];
      }
    });
    builder.addCase(logout.fulfilled, (state) => clearSession(state));
  },
});
/* eslint-enable */

export const {
  decrementCredits,
  setUIEnv,
  setCredits,
  markJoinedClass,
  setXp,
  clearXpPulse,
  setLoginToken,
  clearStoredSession,
  updateUserPremiumProfileExpiresOn,
} = authSlice.actions;

export default authSlice.reducer;
